import { useCallback, useEffect, useMemo, useState } from 'react';
import { organizationNodesSelector } from 'slices/organizationHierarchy.slice';
import { useAppSelector } from 'slices/store';
import {
  type ReturnedUsers,
  exportUsers,
  fetchExportUsersReview,
  userActionFilterSelector,
} from 'slices/user.slice';
import { createUserCSVMapper, exportedUserColumns } from '@timeedit/types';
import { Button, EditableTable, Icon } from '@timeedit/ui-components';
import { Modal, Space } from '@timeedit/ui-components/antd';

export const FILE_NAME = 'user-template.csv';

const wideColumnKeys: Record<string, true> = {
  organizationsAndRoles: true,
  scopes: true,
  appPermissions: true,
  id: true,
  email: true,
  contactEmail: true,
  userObjects: true,
};

export const userTemplateColumns = Object.entries(exportedUserColumns).map(
  ([key, value]) => ({
    title: value,
    key,
    dataIndex: key,
    ...(wideColumnKeys[key]
      ? { width: 280, ellipsis: { showTitle: true } as const }
      : {}),
  })
);

type UserTemplateProps = {
  open: boolean;
  onClose: () => void;
};

export default function UserTemplate({ open, onClose }: UserTemplateProps) {
  const userActionFilter = useAppSelector(userActionFilterSelector);
  const organizations = useAppSelector(organizationNodesSelector);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(true);

  const [usersResponse, setUsersResponse] = useState<ReturnedUsers>({
    results: [],
    count: 0,
    page: 1,
    totalPages: 1,
    totalResults: 0,
  });

  const fetchManyUsersPayload = useMemo(
    () => ({
      ...userActionFilter,
      includeProfilesAsUsers: true,
      limit,
      page,
    }),
    [userActionFilter, limit, page]
  );

  useEffect(() => {
    if (!open) {
      setPage(1);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    fetchExportUsersReview(fetchManyUsersPayload)
      .then((res) => {
        setUsersResponse(res);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [fetchManyUsersPayload, open]);

  const handlePaginationChange = useCallback(
    (page: number, pageSize: number) => {
      setPage(page);
      setLimit(pageSize);
    },
    []
  );

  const downloadCsv = useCallback(() => {
    exportUsers(userActionFilter)
      .then((blob) => {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', FILE_NAME);
        link.click();
        window.URL.revokeObjectURL(link.href);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [userActionFilter]);

  const userCSVMapper = useMemo(
    () =>
      createUserCSVMapper(
        organizations.reduce((acc, org) => {
          acc[org.id] = org.name;
          return acc;
        }, {})
      ),
    [organizations]
  );

  const userData = useMemo(
    () => usersResponse.results.map(userCSVMapper),
    [userCSVMapper, usersResponse.results]
  );

  return (
    <Modal
      title="User Export"
      open={open}
      onCancel={onClose}
      width="80%"
      footer={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            icon={<Icon id="DownloadOutlined" alt="Download" />}
            type="primary"
            onClick={downloadCsv}
          >
            Download
          </Button>
        </Space>
      }
    >
      <div className="external-user-template">
        <EditableTable
          dataSource={userData}
          columns={userTemplateColumns}
          loading={loading}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: page,
            onChange: handlePaginationChange,
            showSizeChanger: false,
            size: 'small',
            total: usersResponse.totalResults,
          }}
        />
      </div>
    </Modal>
  );
}
